// ProductDetailView — marketplace product detail page (PDP).
//
// Extracted from MarketplaceEmbed.tsx (Task 3, TD-269 Sub-project D UX
// split). Owns its own product-detail fetch/state — the parent router no
// longer tracks `selectedProduct`. A fetch failure calls onNotFound() so the
// parent can route back to the catalog, matching the pre-extraction
// behavior of setRoute({ page: "catalog" }) on error.
//
// Task 15 (fixes spec defect D1 — the silent no-op): the Add to Cart click
// used to give NO feedback of any kind.
//
// First cut of this fix inferred the outcome by re-reading cart state after
// `await onAddToCart(...)` resolved (onAddToCart never rejects on EITHER the
// guest or the authenticated path — see AddLineResult's doc comment in
// cart-context.tsx). That inference was UNSOUND, not merely racy: the
// authenticated branch's dispatch runs inside a promise continuation and is
// scheduled through React's Scheduler (a macrotask), which reliably runs
// AFTER the microtask that resumes this component's own `await` — so every
// signed-in add failure rendered "Added to cart.", and a signed-in success
// that followed a prior failure rendered the STALE failure text (the
// OPTIMISTIC_ADD_LINE state it read mid-flight preserves the previous
// `error` until SET_CART/FETCH_SUCCESS lands, one macrotask later). This is
// D1 reappearing on the authenticated path, and it is deterministic, not an
// edge case — every real signed-in add would have hit it.
//
// The fix: onAddToCart now resolves with an EXPLICIT AddLineResult
// ({ ok, error? }) forwarded straight from addLine's own return value (see
// MarketplaceEmbed.tsx's handleAddToCart and AddLineResult's doc comment).
// handleAddToCart below branches on `result.ok` — no re-reading of external
// state, no ordering assumption, no useCart() dependency in this file at
// all. ⚠️ AMENDED (device-count unit, Task 10, rule 48): the ORIGINAL claim
// "no useCart() dependency in this file at all" no longer holds — the
// add-again prompt (design §5) made this view a useCart() consumer (the
// PROMPT DECISION reads the cart's same-product lines; its "more devices"
// arm calls updateLineQty). The Task-15 invariant that matters is UNCHANGED:
// no code path infers an add/update OUTCOME from re-read state — every
// outcome still comes from the explicit AddLineResult. A catch around the
// await remains as a defensive fallback for any future onAddToCart
// implementation that does throw; it is not expected to
// fire for the current production wiring.
//
// Task 12 (spec §7.4): two-column `pdp-grid` layout (mock) — left gallery,
// right buy column (type badge, name, description, price, add-to-cart,
// Specifications table) — plus a related-products section below. Related's
// PRIMARY source is router-lifted catalog data (`catalogProducts`, fed from
// MarketplaceEmbed's Task-11 onCatalogLoaded state); when that source has no
// same-type sibling (deep-link cold load via a #/p/<slug> URL), ONE fallback
// `catalog?product_type=<type>&limit=5` fetch fills it, client-filtered.
// Failures are swallowed — related is an enhancement, never an error surface.
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useEffect, useMemo, useRef, useState } from "react";
import { embedApiFetch, embedApiFetchPage, ApiError } from "../../embed-shared/api-client";
import { useAuth } from "../../embed-shared/auth-context";
import { useCart, type AddLineResult } from "../../embed-shared/cart-context";
import { EmbedBanner, EmbedBadge, EmbedButton, EmbedMarkdown, EmbedSpinner, EmbedText, useEmbedTokens } from "../../embed-shared/primitives";
import type { CartLine, Product } from "../../embed-shared/types";
import { AttentionChip } from "./attention-banner";
import { PricingDisplay } from "./PricingDisplay";
import { ChargeModelCard } from "./ChargeModelCard";
import { ProductCard, typeLabel } from "./ProductCard";
import {
  deriveDisplayPrice,
  chargeToDisplay,
  formatPriceHeadline,
} from "./product-display";
import { StoreDraftForm, StorePicker, type StoreFields } from "./StorePicker";
import { stageStoreDraft } from "./store-staging";
import { useStorefrontConfig } from "./storefront-config";
import { detailInsetStyle, useViewportWidth } from "./detail-inset";

/**
 * Whether adding this product creates a line that RESOLVES a recurring
 * component — the same criterion checkout's store gate keys off (design §6:
 * "the requirement keys off the line resolving a recurring component, not off
 * the product being flagged a subscription"), evaluated on the resolved
 * display pricing: a recurring component on the cell grid (price.components)
 * or, on the legacy path, a charge with a recurring amount. A
 * present-but-EMPTY components list means "priced but currently unresolvable"
 * (I2) — not recurring, and unbuyable anyway.
 */
export function productResolvesRecurring(product: Product): boolean {
  const components = product.price?.components;
  if (components) {
    return components.some((c) => c.charge_type === "recurring");
  }
  return (product.charges ?? []).some((c) => c.recurring_amount != null);
}

export interface ProductDetailViewProps {
  slug: string;
  onBack: () => void;
  /**
   * Adds the product, forwarding the caller's wire options into the real
   * addLine (MarketplaceEmbed.tsx mirrors addLine's own options shape).
   * ⚠️ AMENDED (device-count unit, Task 10, rule 48): the second parameter
   * used to be a bare `storeId` string. ORIGINAL: `onAddToCart: (productId:
   * string, storeId?: string) => Promise<AddLineResult>`. CURRENT: an
   * options object — the add-again flow's "new subscription" arm needs to
   * carry BOTH the chosen store and the explicit new-line intent (design
   * §5) on the SAME add, and a positional string cannot grow a second flag
   * without re-breaking every caller again.
   */
  onAddToCart: (
    productId: string,
    opts?: { storeId?: string; newLine?: boolean },
  ) => Promise<AddLineResult>;
  onNotFound: () => void;
  /**
   * Opens the embed's sign-in flow (device-count unit, Task 10). Used by the
   * add-again prompt's "new subscription" arm when the visitor is signed
   * out: a second same-product line is not representable in the guest
   * staging buffer (it accumulates per product), so rather than stage a line
   * that replay would silently MERGE — the exact failure design §5
   * prohibits — the customer is routed through sign-in and picks up the
   * authenticated flow. Optional: without it the button is not offered
   * signed out.
   */
  onRequireSignIn?: () => void;
  /**
   * Router-lifted catalog products (Task 11's onCatalogLoaded state) — the
   * PRIMARY source for the related section (spec §7.4). Optional: a deep-link
   * cold load has none, and the fallback fetch covers it.
   */
  catalogProducts?: Product[];
  /** Opens another product's PDP (related card click). */
  onOpenProduct?: (slug: string) => void;
}

/** Max related cards rendered (spec §7.4: "≤4, excludes self"). */
const RELATED_MAX = 4;

/**
 * Operator item (p), 2026-08-16 (§41 sibling of the ProductGrid fix): the
 * related strip carried the SAME hard 3-column cap with `1fr` tracks, so on a
 * wide screen its four cards inflated exactly like the catalog's did.
 *
 * Deliberately SMALLER than ProductGrid's GRID_MIN_COLUMN_WIDTH (260px), and
 * that asymmetry is the whole point: this grid lives inside item (a)'s 75%-
 * width container, itself inside item (b)'s 15% detail inset, so a related
 * card sits at ~52% of its pre-(a+b) width — operator-confirmed 2026-08-16.
 * Reusing the catalog's 260px MIN here would fit FEWER columns in that
 * narrow container, and with `1fr` tracks the surviving cards would grow to
 * absorb the space — silently undoing the size reduction the operator
 * explicitly asked for and signed off on. This value is chosen so the column
 * count at normal desktop widths is unchanged from today; only very wide
 * viewports gain columns, which is precisely where the defect lived.
 *
 * `auto-fill` (not `auto-fit`) is also load-bearing for the same reason: with
 * at most RELATED_MAX cards, `auto-fit` would collapse the empty tracks and
 * let those few cards stretch across the container — again re-inflating them.
 * `auto-fill` keeps the empty tracks, so the cards stay at their intended size
 * and stay left-aligned to the section heading, as item (a) established.
 */
const RELATED_MIN_COLUMN_WIDTH = 190;

/**
 * Viewport width at which the related strip becomes width-driven — HIGHER
 * than ProductGrid's 768px, and the gap is the item-(a) safeguard.
 *
 * This grid is only ~52% as wide as the catalog's (item (a)'s 75% container
 * inside item (b)'s 15% inset), so at a laptop viewport its three columns are
 * already narrow — around 160px at 1024px. Switching to auto-fill there would
 * fit only TWO columns of the 190px minimum, and with `1fr` tracks those two
 * cards would then grow to ~257px: BIGGER than today, silently undoing the
 * size reduction the operator asked for and signed off on.
 *
 * Holding the existing 3-column step until 1280px avoids that entirely:
 *   - below 1280 the rendered result is byte-identical to today (3 columns);
 *   - at 1280 the switchover is continuous — auto-fill still resolves to 3
 *     columns there, so nothing jumps at the boundary;
 *   - above it, extra width buys columns and the card width falls away from
 *     today's (at 2560px: ~204px here versus ~432px before this fix).
 * Net effect: a related card is never WIDER than it is today at any viewport,
 * which is the invariant item (a) actually cares about.
 */
const RELATED_FLUID_MIN_VIEWPORT = 1280;

/**
 * Track list for the related strip. Mirrors ProductGrid's small-screen ladder
 * (1 col < 480, 2 cols 480–767), keeps the historical 3-column step through
 * 1279, and goes width-driven from RELATED_FLUID_MIN_VIEWPORT up. Exported
 * for the co-located test (§55).
 */
export function relatedGridColumns(width: number): string {
  if (width >= RELATED_FLUID_MIN_VIEWPORT) {
    return `repeat(auto-fill, minmax(${RELATED_MIN_COLUMN_WIDTH}px, 1fr))`;
  }
  const columns = width >= 768 ? 3 : width >= 480 ? 2 : 1;
  return `repeat(${columns}, minmax(0, 1fr))`;
}

/** How long the "Added to cart." confirmation stays on screen. */
const ADDED_CONFIRMATION_MS = 4000;

/**
 * Viewport width for the responsive `pdp-grid` (mock's 900px breakpoint) and
 * the related-card grid — now the shared detail-inset hook (four consumers:
 * PDP, cart, checkout, order confirmation; operator items a+b 2026-08-16).
 * Lives in detail-inset.ts.
 */
export function ProductDetailView({ slug, onBack, onAddToCart, onNotFound, catalogProducts, onOpenProduct, onRequireSignIn }: ProductDetailViewProps) {
  const t = useEmbedTokens();
  const width = useViewportWidth();
  const { token } = useAuth();
  // Add-again (Task 10, design §5): the prompt decision needs the cart's
  // same-product lines and, for its "more devices" arm, the line-update
  // mutation. This makes the PDP a useCart consumer — Task 15's
  // no-useCart() note in this file's header is amended in the same diff
  // (rule 48): that fix was about not inferring an add's OUTCOME from
  // re-read state (still true — every outcome here comes from the explicit
  // AddLineResult), while the PROMPT DECISION is a legitimate read of the
  // freshest cart the SPA has. A not-yet-loaded cart (null) reads as "not
  // in the cart" and the plain add flow runs — exactly today's behavior
  // for the first moments after mount.
  const { state: cartState, updateLineQty } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // Store association flow (device-count unit, Task 9, design §6): a product
  // that resolves a recurring component gathers its store BEFORE the add —
  // signed IN via the rule-66 StorePicker (the add then carries store_id on
  // the Task-7 wire); signed OUT via a client-staged draft (ruling 9(a))
  // that the post-login reconciliation in CartView confirms. A one-time
  // product never enters either flow (ruling 10 — no store required).
  const [storeFlow, setStoreFlow] = useState<"none" | "picker" | "draft">("none");
  // WHICH add the picker is gathering a store for (Task 10): the first add
  // of a recurring product (Task 9's flow) or the add-again prompt's "new
  // subscription" arm (which must carry the new-line intent, design §5).
  const [pickerIntent, setPickerIntent] = useState<"first-add" | "new-line">("first-add");
  // The add-again prompt (design §5, ruling 3): open after the Add click
  // when the product is already in the cart AND resolves a recurring
  // component — the two intents ("more devices" vs "new subscription at
  // another store") diverge only for those, so only those are asked about.
  const [addAgainOpen, setAddAgainOpen] = useState(false);
  // Store names for labeling per-line actions when the SAME product spans
  // MULTIPLE cart lines (⚠️ AMENDED, device-count unit close-out:
  // ORIGINAL said "only reachable once the server honors the new_line
  // intent" — the server now HONORS it, the cart-add reader landed with
  // the bare-add ambiguity rejection, so this state is reachable today):
  // each line's "more devices" action is labeled by its
  // store's NAME (rule-66 spirit), never a raw line/store id.
  const [storeNamesById, setStoreNamesById] = useState<Record<string, string>>({});
  // Shown for one confirmation window after a staged-draft add: the line is
  // in the guest cart, and the store is CONFIRMED after sign-in (never
  // silently associated — design §6.2).
  const [draftStagedNote, setDraftStagedNote] = useState(false);
  // Index into `product.images` of the currently-selected gallery shot.
  // Reset to 0 whenever the product changes (the second effect below already
  // clears the other transient state on `slug` change, but it does not touch
  // selectedImage — leaving a stale index would render image[N] of the NEW
  // product as the main shot, which is wrong if the new product has fewer
  // images. Safe to reset unconditionally here: the component remounts per
  // product via the slug route, AND the explicit reset pins it for any
  // future layout that reuses the instance (project rule §13 — defensive).
  const [selectedImage, setSelectedImage] = useState(0);
  // TD-324: operator-configured marketplace-wide placeholder, attempted only
  // when the selected gallery image is absent or fails to load. Failures are
  // tracked by URL (not tier position) so a URL that fails once is skipped at
  // every subsequent tier check, mirroring ProductCard.tsx's fallback chain.
  // ITEM A fix (fix wave 2026-09-15, operator ruling 2026-09-15): currency is
  // marketplace-wide, never per-product — read once here and forwarded to
  // every price-formatting call below instead of a per-charge wire currency.
  const { currency, placeholder_image_url: placeholderUrl } = useStorefrontConfig();
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());

  // Related products (spec §7.4): `fallbackRelated` holds the one-shot
  // fallback fetch's result; the primary source (catalogProducts) always
  // wins when it covers the section. `relatedFetchedFor` pins the fallback
  // to at most ONE fetch per loaded product — re-renders, add-to-cart
  // feedback state flips, and parent re-renders must not re-fire it.
  const [fallbackRelated, setFallbackRelated] = useState<Product[]>([]);
  const relatedFetchedFor = useRef<string | null>(null);

  // Primary source: same product_type, excluding self, capped at 4.
  const primaryRelated = useMemo(() => {
    if (!product) return [];
    return (catalogProducts ?? [])
      .filter((p) => p.product_type === product.product_type && p.id !== product.id)
      .slice(0, RELATED_MAX);
  }, [catalogProducts, product]);

  // Load product detail
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Go handler: HandleCatalogGet emits {"data": productResp}.
        // embedApiFetch unwraps .data → Product directly. (§39 verified)
        const data = await embedApiFetch<Product>(`/api/v1/marketplace/catalog/${slug}`);
        if (!cancelled) setProduct(data);
      } catch {
        if (!cancelled) onNotFound();
      }
    })();
    return () => { cancelled = true; };
    // Deliberately keyed on `slug` only (matches the pre-extraction effect,
    // which was keyed on the whole `route` object — equivalent to `slug`
    // changing). `onNotFound` is an inline callback at the call site and is
    // NOT included here: including it would refetch on every parent
    // re-render (a behavior change), since inline arrow props get a new
    // reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // A route change to a different product clears any transient add-to-cart
  // feedback from the PREVIOUS product — it must not persist across
  // navigation (brief step 3). `failed` (TD-324, fix round 1) must reset here
  // too: the configured placeholder URL is the SAME across every product, so
  // a transient failure recorded while viewing product A would otherwise mark
  // that placeholder permanently broken for every other product viewed in the
  // same mounted instance.
  useEffect(() => {
    setAdding(false);
    setAdded(false);
    setAddError(null);
    setStoreFlow("none");
    setPickerIntent("first-add");
    setAddAgainOpen(false);
    setDraftStagedNote(false);
    setSelectedImage(0);
    setFailed(new Set());
    // Product→product hop (related card): the fallback's result for the
    // PREVIOUS product must not flash under the new one while its own fetch
    // is in flight, and the once-guard must re-arm for the new product.
    setFallbackRelated([]);
    relatedFetchedFor.current = null;
  }, [slug]);

  // Related fallback (spec §7.4): fires ONLY when the primary source has no
  // same-type sibling (deep-link cold load), at most once per product. The
  // response can include the product itself (limit=5 leaves room for it) —
  // filtered client-side. Any failure resolves to "no related section":
  // related is an enhancement, never an error surface (also covers a
  // non-promise/non-envelope mock or wire skew — `page.data` access sits
  // inside the try).
  useEffect(() => {
    if (!product) return;
    if (primaryRelated.length > 0) return;
    if (relatedFetchedFor.current === product.id) return;
    relatedFetchedFor.current = product.id;
    let cancelled = false;
    (async () => {
      try {
        const page = await embedApiFetchPage<Product[]>("/api/v1/marketplace/catalog", {
          query: { product_type: product.product_type, limit: 5 },
        });
        if (cancelled) return;
        setFallbackRelated(
          (page.data ?? [])
            .filter((p) => p.product_type === product.product_type && p.id !== product.id)
            .slice(0, RELATED_MAX),
        );
      } catch {
        if (!cancelled) setFallbackRelated([]);
      }
    })();
    return () => { cancelled = true; };
    // primaryRelated.length (a primitive), not the memoized array — the
    // fetch trigger is "does the primary source cover it", not its identity.
    // `slug` is here so a mid-fetch URL hop cancels the in-flight fallback
    // for the PREVIOUS product (the cleanup flags it before its result can
    // flash under the new product) and the effect re-fires for the new one;
    // the slug-keyed reset above re-arms the once-guard in between.
  }, [product, primaryRelated.length, slug]);

  // Auto-dismiss the success confirmation after a few seconds.
  useEffect(() => {
    if (!added) return;
    const id = setTimeout(() => setAdded(false), ADDED_CONFIRMATION_MS);
    return () => clearTimeout(id);
  }, [added]);

  // The cart's existing lines for THIS product (design §5's add-again
  // predicate). Computed from the context's freshest cart — null cart reads
  // as none. Multiple same-product lines are the shape the add-again
  // prompt's "new subscription" arm creates (one subscription per store).
  const sameProductLines: CartLine[] = useMemo(() => {
    if (!product || !cartState.cart) return [];
    return cartState.cart.lines.filter((l) => l.product_id === product.id);
  }, [cartState.cart, product]);

  // Store names for the multi-line prompt labels — fetched ONLY when the
  // prompt is open over more than one line (signed in). Cosmetic: a failed
  // fetch degrades to the positional label, never blocks the flow.
  useEffect(() => {
    if (!addAgainOpen || !token || sameProductLines.length <= 1) return;
    let cancelled = false;
    (async () => {
      try {
        const { listStores } = await import("./store-api");
        const stores = await listStores();
        if (cancelled) return;
        const byId: Record<string, string> = {};
        for (const s of stores) byId[s.id] = s.name;
        setStoreNamesById(byId);
      } catch {
        // Cosmetic degradation only — the positional fallback labels below.
      }
    })();
    return () => { cancelled = true; };
  }, [addAgainOpen, token, sameProductLines.length]);

  // The actual add, shared by every path. storeId rides the Task-7 cart-add
  // wire when the customer chose a store; newLine (Task 10, design §5)
  // carries the add-again prompt's explicit "new subscription" intent.
  // Resolves whether the add succeeded — the draft-staged note below must
  // not claim a store confirmation for a line that never landed.
  async function addWithStore(opts?: { storeId?: string; newLine?: boolean }): Promise<boolean> {
    if (!product) return false;
    setAdding(true);
    setAdded(false);
    setAddError(null);
    try {
      const result = await onAddToCart(product.id, opts);
      if (result.ok) {
        setAdded(true);
        return true;
      }
      setAddError(result.error ?? "Something went wrong. Please try again.");
      return false;
    } catch (err) {
      // Defensive fallback only — the production onAddToCart (addLine,
      // forwarded by MarketplaceEmbed.tsx) never rejects. Kept in case a
      // future implementation does.
      setAddError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      return false;
    } finally {
      setAdding(false);
    }
  }

  // The add-again prompt's increment arm (design §5): the choice travels as
  // a line-update request on the NAMED line — the server has no merge
  // decision to make, and the increment arithmetic (existing + added) is
  // the client's explicit expression. Signed OUT, the guest buffer already
  // accumulates per product, so the arm is today's staging add.
  async function addDevicesToLine(line: CartLine) {
    setAddAgainOpen(false);
    if (!token) {
      await addWithStore();
      return;
    }
    setAdding(true);
    setAdded(false);
    setAddError(null);
    try {
      const result = await updateLineQty(line.id, line.quantity + 1);
      if (result.ok) setAdded(true);
      else setAddError(result.error ?? "We couldn't add that device. Please try again.");
    } finally {
      setAdding(false);
    }
  }

  // Requires-store gate (design §6) + the add-again gate (design §5, ruling
  // 3): a recurring product ALREADY in the cart asks FIRST which intent the
  // customer means — the server cannot tell a bare add's meaning (it merges
  // same-product adds unconditionally), so the ambiguous bare add is made
  // unrepresentable from this UI. While a flow is open, further Add clicks
  // are inert — falling through would add a recurring line with NO store,
  // which checkout then refuses (Task 8's 422).
  async function handleAddToCart() {
    if (!product) return;
    if (productResolvesRecurring(product)) {
      if (storeFlow !== "none" || addAgainOpen) return;
      if (sameProductLines.length > 0) {
        setAddAgainOpen(true);
        return;
      }
      setPickerIntent("first-add");
      setStoreFlow(token ? "picker" : "draft");
      return;
    }
    // Non-subscription product: the two add-again intents do not diverge (a
    // repeated one-time add just means "more"), so today's increment add
    // runs with no prompt (ruling 3 tail).
    await addWithStore();
  }

  // Signed IN: the picked (or quick-added) store goes straight onto the add.
  // The add-again prompt's "new subscription" arm opened the picker with the
  // new-line intent; the first-add arm keeps today's plain add.
  function handlePickedStore(storeId: string) {
    const intent = pickerIntent;
    setStoreFlow("none");
    setPickerIntent("first-add");
    void addWithStore(intent === "new-line" ? { storeId, newLine: true } : { storeId });
  }

  // Signed OUT (ruling 9(a)): STAGE the captured store client-side — no API
  // call is possible signed out — then stage the line in the guest cart as
  // always. The reconciliation in CartView shows the candidate and lets the
  // customer CONFIRM after login; nothing is ever auto-associated.
  function handleCapturedDraft(fields: StoreFields) {
    if (!product) return;
    const staged = stageStoreDraft({ product_id: product.id, ...fields });
    setStoreFlow("none");
    if (!staged) {
      setAddError("We couldn't save that store. Please try again.");
      return;
    }
    void (async () => {
      const ok = await addWithStore();
      if (ok) setDraftStagedNote(true);
    })();
  }

  if (!product) {
    return (
      <p role="status" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: t.spacing.sm, color: t.colors.textSecondary, textAlign: "center" }}>
        <EmbedSpinner />
        <EmbedText tone="secondary">Loading...</EmbedText>
      </p>
    );
  }
  // Derive display values from the catalog wire shape. `price.components[]`
  // (R2 catalog-display redesign, design doc §5) is the new source when
  // present; `charges[]` (decimal dollar strings) remains the fallback for
  // products with no price cells. ⚠️ Corrected (Minor fix, fix wave
  // 2026-09-15): this comment used to claim "there is no flat price" — stale
  // the instant `price.components[]` shipped, and self-contradicting the
  // sentence right before it in this same comment. There is still no flat
  // `images`/`charge_model` (§39); `price` is very much a thing now.
  const priceComponents = product.price?.components ?? [];
  const hasPriceComponents = priceComponents.length > 0;
  // I2 fix (fix wave 2026-09-15, "server fail-closed and client fail-closed
  // do not compose"): `price` being PRESENT with an EMPTY components[] means
  // the server resolved this product as cell-priced but currently
  // unresolvable (customer_handler.go's resolveCatalogPrice — "priced but
  // unresolvable," never "not cell-priced") and must NOT fall back to
  // charges[], which can be a stale, pre-cell-grid amount for a product
  // genuinely configured to be cell-priced. `hasPriceComponents` alone
  // conflates "absent" and "present-but-empty" into the same falsy check —
  // both gates below now key on priceIsAbsent instead, so ONLY a genuinely
  // absent `price` key falls back to the legacy charges[] path.
  const priceIsAbsent = product.price == null;
  const headline = hasPriceComponents ? formatPriceHeadline(product.price, product.charges, currency) : null;
  // Legacy headline path — used only when `price` is absent (kept exactly
  // as before, incl. PricingDisplay's own strikethrough/isFromPrice
  // rendering, for zero behavior change on existing charges[]-only products).
  const price = deriveDisplayPrice(product.charges);
  const displayCharges = (product.charges ?? []).map(chargeToDisplay);
  // Show the per-charge breakdown only when it adds detail beyond the
  // headline price. price.components[]: each entry is EXACTLY one
  // charge_type (never both), so "multiple components" is the only signal.
  // charges[] (legacy): multiple charges, or a charge that is both one-time
  // and recurring — gated on priceIsAbsent (I2 fix) so a present-but-empty
  // `price` never renders a legacy breakdown either.
  //
  // ⚠️ AMENDED (device-count unit, Task 10, 2026-09-17) — back to the RAW
  // component count, both fix-wave gates RETIRED with the predicates they
  // used (see the REMOVED record in product-display.ts). ORIGINAL gates (fix
  // waves 2026-09-15 ITEM C / 2026-09-16): the count was filtered through
  // `isCurrentlyBillable` (device-basis one-time suppressed — it billed $0)
  // and gated on `isCurrentlyPurchasable` (no breakdown under a "Contact
  // for pricing" headline for an unbuyable product). CURRENT: a device-basis
  // one-time component bills quantity × rate for real (ruling 1) and recurring
  // products are sellable again (Task 5), so every component belongs in the
  // breakdown — a {store one-time, device one-time} product shows BOTH rows,
  // which is the display-vs-billing agreement design §8.3 requires.
  const showBreakdown = hasPriceComponents
    ? priceComponents.length > 1
    : priceIsAbsent &&
      (displayCharges.length > 1 ||
        displayCharges.some((c) => c.oneTime != null && c.recurring != null));
  // Specifications (D4): LENGTH-based presence check, never key-presence —
  // the detail edge case can emit "specs":[] (empty array, not absent), and
  // a presence-only check would render a section heading over an empty
  // table (T6 review note, recorded in types.ts's Product.specs doc).
  const specs = product.specs ?? [];
  // Related section source: primary (catalog data) wins; the fallback only
  // fills when the primary has nothing (spec §7.4).
  const related = primaryRelated.length > 0 ? primaryRelated : fallbackRelated;
  return (
    <div
      style={{
        // Operator items a+b, 2026-08-16: the shared detail-view inset —
        // 15%/15% at >=900px against the full-width catalog (small fixed
        // gutter below). Same helper as cart/checkout/order-confirmation.
        ...detailInsetStyle(t, width),
      }}
    >
      <div style={{ marginBottom: t.spacing.md }}>
        <EmbedButton type="button" variant="ghost" size="sm" onClick={onBack}>
          &larr; Back to catalog
        </EmbedButton>
      </div>
      {/* Two-column pdp-grid (mock §7.4): gallery left, buy column right;
          collapses to a single column under the mock's 900px breakpoint.
          alignItems:start keeps a tall gallery from stretching the buy
          column (and vice versa). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: width >= 900 ? "minmax(0, 7fr) minmax(0, 5fr)" : "minmax(0, 1fr)",
          gap: t.spacing.lg,
          alignItems: "start",
        }}
      >
        {/* TD-270: render product media (sorted by sort_order on the wire) as
            a main image + selectable thumbnail rail. alt_text is *string on
            the wire and may be null — fall back to the product name (§39).
            TD-324: three-tier fallback for the main image, mirroring
            ProductCard.tsx — tier 1 the selected/server image, tier 2 the
            operator-configured marketplace-wide placeholder (attempted only
            when tier 1 is absent or its onError has fired), tier 3 (unique to
            this view, unlike ProductCard's branded box) is no media area at
            all — reached ONLY when there are no images at all AND the
            placeholder is either not configured OR has itself previously
            failed (is already in `failed`) (the pre-TD-324 behavior for the
            no-images-no-placeholder case, now also covering the
            placeholder-itself-failed case). If the product HAS images but
            every candidate URL has
            failed and there is no placeholder to substitute, `src` falls
            back to the original (broken) URL so the wrapper and thumbnail
            rail stay mounted and the `<img>` shows its native broken-image
            state — exactly as it did before this change, when there was no
            onError handler at all (fix round 1: `return null` here used to
            also fire on "images exist but the selected one failed", which
            wrongly discarded the whole gallery, including sibling images
            that load fine). The thumbnail rail is driven by `images.length`,
            independent of which tier the main image is showing, so a
            substituted placeholder never produces a one-item rail. */}
        {(() => {
          const images = product.images ?? [];
          const main = images[selectedImage] ?? images[0];
          const src =
            [main?.url, placeholderUrl].find(
              (u): u is string => typeof u === "string" && u !== "" && !failed.has(u),
            ) ?? main?.url;
          if (!src) return null;
          return (
            <div style={{ display: "flex", gap: t.spacing.md }}>
              <img
                src={src}
                // Tier 1 (the selected/server image) carries its own alt
                // text, falling back to the product name (§39, unchanged).
                // Tier 2 (the configured placeholder) is always decorative —
                // it never describes this specific product — so alt is a
                // literal "", never product.name.
                alt={src === main?.url ? (main.alt_text ?? product.name) : ""}
                data-testid="pdp-main-image"
                onError={() => setFailed((prev) => (prev.has(src) ? prev : new Set(prev).add(src)))}
                style={{ width: 320, height: 320, objectFit: "cover", borderRadius: t.borderRadius, flexShrink: 0 }}
              />
              {images.length > 1 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm, overflowY: "auto", maxHeight: 320 }}>
                  {images.map((img, i) => (
                    // raw <button>: gallery thumb with selected-ring; kept
                    // (primitive sweep TD-NEW). EmbedButton's fixed height +
                    // padding would regress the compact thumbnail tile, and
                    // the selected-state border ring is a style EmbedButton
                    // cannot express without a new variant.
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => setSelectedImage(i)}
                      aria-label={`Image ${i + 1}`}
                      aria-pressed={i === selectedImage}
                      data-testid={`pdp-thumb-${i}`}
                      style={{
                        padding: 0,
                        border: i === selectedImage
                          ? `2px solid ${t.colors.primary}`
                          : `2px solid ${t.colors.border}`,
                        borderRadius: t.borderRadius,
                        cursor: "pointer",
                        background: "none",
                      }}
                    >
                      <img
                        src={img.url}
                        alt={img.alt_text ?? product.name}
                        loading="lazy"
                        style={{ width: 64, height: 64, objectFit: "cover", borderRadius: t.borderRadius, display: "block" }}
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })()}
        <div>
          {/* D1 type badge — the canonical label (typeLabel), never the raw
              lowercase product_type string. The attention banner chip
              (2026-08-16) joins it on the same tag row, directly above the
              name heading (document order: before the <h2>) — grouping the
              two tags keeps the large heading's accessible name clean rather
              than folding the banner text into it. Only when set. */}
          <div
            style={{
              marginBottom: t.spacing.xs,
              display: "flex",
              alignItems: "center",
              gap: t.spacing.xs,
              flexWrap: "wrap",
            }}
          >
            <EmbedBadge tone="neutral">{typeLabel(product.product_type)}</EmbedBadge>
            {product.attention_label ? (
              <AttentionChip label={product.attention_label} color={product.attention_color} />
            ) : null}
          </div>
          <h2 style={{ fontSize: t.fontSize.xl, fontWeight: 700, color: t.colors.text, margin: `0 0 ${t.spacing.sm}px 0` }}>
            {product.name}
          </h2>
          {/* Descriptions are markdown — the AI generate prompt asks the model
              for it (ai_generate_service.go) and operators can hand-write it.
              Printing the raw string into a <p> folded every newline and left
              literal ## and ** on screen. EmbedMarkdown's own trailing block
              margin is spacing.sm, so the wrapper adds the remaining sm to
              preserve the original spacing.md gap below the description. */}
          {product.description && (
            <div style={{ marginBottom: t.spacing.sm }}>
              <EmbedMarkdown source={product.description} />
            </div>
          )}
          {hasPriceComponents && headline ? (
            // R2 catalog-display redesign (design doc §4): formatPriceHeadline
            // returns the COMPLETE string (prefix + amount + suffix already
            // assembled) — rendered directly rather than decomposed back into
            // PricingDisplay's separate amount/currency/recurringLabel props,
            // which would require re-splitting a string this formatter exists
            // specifically to keep from being hand-assembled at call sites.
            <p style={{ fontSize: t.fontSize.lg, fontWeight: 700, color: t.colors.text, margin: 0 }}>
              {headline}
            </p>
          ) : priceIsAbsent && price ? (
            // ITEM A fix (fix wave 2026-09-15): `currency` here is the global
            // StorefrontConfig value — historically NOT `price.currency`
            // (deriveDisplayPrice's per-CHARGE currency, sourced from the
            // legacy `charges[].currency` wire field), which TD-513 Task 4
            // (2026-09-19) then removed from the display shape entirely; the
            // ruling retires per-product currency as an authority.
            <PricingDisplay
              amount={price.amount}
              currency={currency}
              recurringLabel={price.recurringLabel}
              isFromPrice={price.isFromPrice}
            />
          ) : (
            <p style={{ fontSize: t.fontSize.md, color: t.colors.textSecondary, margin: 0 }}>
              Contact for pricing
            </p>
          )}
          {showBreakdown && (
            <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm, marginTop: t.spacing.md }}>
              {hasPriceComponents ? (
                <ChargeModelCard components={priceComponents} />
              ) : (
                displayCharges.map((c) => (
                  <ChargeModelCard
                    key={c.id}
                    chargeModel={{
                      one_time_amount: c.oneTime ?? 0,
                      recurring_amount: c.recurring ?? 0,
                      recurring_interval: c.recurringInterval ?? "",
                      // ITEM A fix (fix wave 2026-09-15) forwarded the global
                      // StorefrontConfig currency here for readability;
                      // TD-513 Task 4 (2026-09-19) removed the dormant
                      // `ChargeModel.currency` member entirely — ChargeModelCard
                      // reads useStorefrontConfig() itself and never read this
                      // field even when it existed.
                    }}
                  />
                ))
              )}
            </div>
          )}
          {/* Kept permanently mounted (TD-290(d) pattern) rather than
              mounting the live region together with its content — some
              assistive-tech engines only reliably announce a live region
              that already existed before its content changed. */}
          <div aria-live="polite" style={{ marginTop: added ? t.spacing.sm : 0 }}>
            {added && <EmbedBanner tone="success">Added to cart.</EmbedBanner>}
          </div>
          <div aria-live="assertive" style={{ marginTop: addError ? t.spacing.sm : 0 }}>
            {addError && <EmbedBanner tone="error">{addError}</EmbedBanner>}
          </div>
          <div style={{ marginTop: t.spacing.lg }}>
            <EmbedButton
              type="button"
              variant="primary"
              fullWidth
              loading={adding}
              onClick={handleAddToCart}
            >
              Add to cart
            </EmbedButton>
          </div>
          {/* Add-again prompt (Task 10, design §5, ruling 3): shown after the
              Add click when a recurring product is already in the cart. The
              choice travels in the request that follows — "more devices" as a
              line-update PATCH on the named line, "new subscription" as an
              add carrying the explicit new-line intent — so the server never
              has to infer (or silently merge) either meaning. */}
          {addAgainOpen && (
            <section
              aria-label="Already in your cart"
              style={{
                marginTop: t.spacing.md,
                padding: t.spacing.md,
                border: `1px solid ${t.colors.borderSubtle}`,
                borderRadius: t.borderRadius,
                display: "flex",
                flexDirection: "column",
                gap: t.spacing.sm,
              }}
            >
              <h4 style={{ margin: 0, fontSize: t.fontSize.md, fontWeight: 700, color: t.colors.text }}>
                Already in your cart
              </h4>
              <EmbedText size="sm" tone="secondary">
                {sameProductLines.length > 1
                  ? "You have several subscriptions for this product. Add devices to one of them, or start a new subscription for another store."
                  : "Add more devices to your subscription, or start a new subscription for another store."}
              </EmbedText>
              {sameProductLines.map((line, i) => {
                // Multi-line labels carry the store's NAME (rule-66 spirit),
                // degrading to a positional label until the names load or on
                // a failed fetch. A store-less line says so — that state
                // genuinely needs disambiguating from a named one.
                const label =
                  sameProductLines.length > 1
                    ? `Add devices — ${line.store_id ? (storeNamesById[line.store_id] ?? `subscription ${i + 1}`) : "no store yet"}`
                    : "Add devices to your subscription";
                return (
                  <EmbedButton
                    key={line.id}
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={adding}
                    disabled={adding}
                    onClick={() => void addDevicesToLine(line)}
                  >
                    {label}
                  </EmbedButton>
                );
              })}
              {token ? (
                <EmbedButton
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={adding}
                  onClick={() => {
                    // Ruling 9: the store is gathered FIRST — the add (with
                    // its new-line intent) is sent only after a store is
                    // picked/quick-added.
                    setAddAgainOpen(false);
                    setPickerIntent("new-line");
                    setStoreFlow("picker");
                  }}
                >
                  New subscription — another store
                </EmbedButton>
              ) : onRequireSignIn ? (
                <EmbedButton type="button" variant="primary" size="sm" onClick={onRequireSignIn}>
                  New subscription — sign in to continue
                </EmbedButton>
              ) : null}
              <EmbedButton
                type="button"
                variant="ghost"
                size="sm"
                disabled={adding}
                onClick={() => setAddAgainOpen(false)}
              >
                Cancel
              </EmbedButton>
            </section>
          )}
          {/* Store association flow (Task 9): rendered only while a flow is
              open — i.e. only for a product that resolves a recurring
              component. One-time products never mount either branch. */}
          {storeFlow === "picker" && (
            <StorePicker
              onPick={(store) => handlePickedStore(store.id)}
              onCancel={() => setStoreFlow("none")}
            />
          )}
          {storeFlow === "draft" && (
            <StoreDraftForm
              onCapture={handleCapturedDraft}
              onCancel={() => setStoreFlow("none")}
            />
          )}
          {draftStagedNote && (
            <div aria-live="polite" style={{ marginTop: t.spacing.sm }}>
              <EmbedBanner tone="success">
                We&apos;ll confirm your store when you sign in.
              </EmbedBanner>
            </div>
          )}
          {/* Specifications table (D4): label→value rows, one <tr> per spec
              pair, hidden entirely when specs is absent OR empty (see the
              length-based `specs` derivation above). No <thead> — the label
              column IS the row header (th scope="row"). */}
          {specs.length > 0 && (
            <section style={{ marginTop: t.spacing.lg }}>
              <h3
                style={{
                  fontSize: t.fontSize.md,
                  fontWeight: 700,
                  color: t.colors.text,
                  margin: `0 0 ${t.spacing.sm}px 0`,
                }}
              >
                Specifications
              </h3>
              <table
                data-testid="pdp-specs"
                style={{ width: "100%", borderCollapse: "collapse", fontSize: t.fontSize.sm }}
              >
                <tbody>
                  {specs.map((s, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${t.colors.borderSubtle}` }}>
                      <th
                        scope="row"
                        style={{
                          textAlign: "left",
                          verticalAlign: "top",
                          padding: `${t.spacing.xs}px ${t.spacing.md}px ${t.spacing.xs}px 0`,
                          color: t.colors.textSecondary,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.label}
                      </th>
                      <td
                        style={{
                          padding: `${t.spacing.xs}px 0`,
                          color: t.colors.text,
                          lineHeight: t.lineHeight.normal,
                        }}
                      >
                        {s.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      </div>
      {/* Related products (spec §7.4): same product_type, excludes self, ≤4.
          Rendered ONLY when a source produced at least one sibling — an
          empty related list must not leave a dangling section heading. The
          heading lowercases the canonical D1 label ("Related hardware"),
          mirroring the mock. Cards reuse ProductCard so the related grid and
          the catalog grid present identically. */}
      {related.length > 0 && (
        <section style={{ marginTop: t.spacing.xl }}>
          <h3
            style={{
              fontSize: t.fontSize.md,
              fontWeight: 700,
              color: t.colors.text,
              margin: `0 0 ${t.spacing.md}px 0`,
            }}
          >
            Related {typeLabel(product.product_type).toLowerCase()}
          </h3>
          <div
            style={{
              display: "grid",
              // Item (p): width-driven above 768px — was a hard 3-column cap
              // that inflated these cards on wide screens. MIN is tuned to
              // preserve item (a)'s sizing; see RELATED_MIN_COLUMN_WIDTH.
              gridTemplateColumns: relatedGridColumns(width),
              gap: t.spacing.lg,
              // Operator item (a), 2026-08-16: related tiles at 75% of their
              // former size — the grid occupies 75% of the PDP content width,
              // so each fluid cell (and its 4:3 image) is exactly 75%.
              // Left-aligned to the section heading. NOTE: composes with (b)'s
              // 15% inset — a desktop card lands at ~52% of its pre-(a+b)
              // width, operator-confirmed 2026-08-16. Item (p) preserves this:
              // the 75% container is untouched and the auto-fill MIN is chosen
              // so the column count here does not change at desktop widths.
              width: "75%",
            }}
          >
            {related.map((p) => (
              <ProductCard key={p.id} product={p} onClick={() => onOpenProduct?.(p.slug)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
